<template>
  <div class="flex flex-col h-[100dvh] bg-slate-50 font-sans overflow-hidden">
    <header
      class="shrink-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shadow-sm z-30"
    >
      <div class="flex items-center gap-3 sm:gap-4">
        <span class="bg-blue-600 p-1.5 rounded-lg text-white shadow-sm shrink-0">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
        </span>

        <OrgSwitcher />
      </div>

      <div class="flex items-center shrink-0">
        <UserButton after-sign-out-url="/auth/sign-in" />
      </div>
    </header>

    <nav class="hidden sm:flex bg-white border-b border-slate-200 px-6 gap-8 shrink-0 z-20">
      <RouterLink
        v-for="tab in tabs"
        :key="tab.name"
        :to="{ name: tab.routeName }"
        class="py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2"
        :class="
          isRouteActive(tab.routeName)
            ? 'border-blue-600 text-blue-600'
            : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
        "
      >
        <div v-html="tab.icon" class="w-5 h-5 [&>svg]:w-full [&>svg]:h-full"></div>
        {{ tab.name }}
      </RouterLink>
    </nav>

    <main class="flex-1 overflow-y-auto bg-slate-50 relative w-full max-w-7xl mx-auto">
      <RouterView />
    </main>

    <nav
      class="sm:hidden shrink-0 bg-white border-t border-slate-200 flex justify-around items-center pt-2 pb-safe px-2 z-40"
    >
      <RouterLink
        v-for="tab in tabs"
        :key="'mobile-' + tab.name"
        :to="{ name: tab.routeName }"
        class="flex flex-col items-center justify-center w-full min-h-[3.5rem] transition-all"
        :class="
          isRouteActive(tab.routeName) ? 'text-blue-700' : 'text-slate-500 hover:text-slate-800'
        "
      >
        <div
          class="flex items-center justify-center w-14 h-8 rounded-full transition-colors duration-200 mb-1"
          :class="isRouteActive(tab.routeName) ? 'bg-blue-100/60' : 'bg-transparent'"
        >
          <div
            v-html="tab.icon"
            class="w-5 h-5 transition-all [&>svg]:w-full [&>svg]:h-full"
            :class="
              isRouteActive(tab.routeName)
                ? '[&>svg]:stroke-[2.5px] scale-110'
                : '[&>svg]:stroke-[2px]'
            "
          ></div>
        </div>

        <span
          class="text-[10px] tracking-wide transition-all"
          :class="isRouteActive(tab.routeName) ? 'font-bold' : 'font-medium'"
        >
          {{ tab.name }}
        </span>
      </RouterLink>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { UserButton, useOrganization } from '@clerk/vue'
import OrgSwitcher from '@/modules/auth/organizations/components/OrgSwitcher.vue'

const route = useRoute()
const { membership } = useOrganization()

const isRouteActive = (routeName) => {
  return route.name === routeName
}

const isSubcontractor = computed(() => {
  return membership.value?.role === 'org:subcontractor'
})

const CalendarIcon = `
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
`

const ClockIcon = `
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
`

const PlaneIcon = `
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
`

const payrollIcon = `
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
`

// Make tabs computed so it reacts to the user's role
const tabs = computed(() => {
  const allTabs = [
    {
      name: 'My Schedule',
      routeName: 'FieldSchedule',
      icon: CalendarIcon,
    },
    {
      name: 'Time Tracking',
      routeName: 'FieldTimeTracking',
      icon: ClockIcon,
    },
    {
      name: 'Time Off',
      routeName: 'FieldTimeOff',
      icon: PlaneIcon,
    },
    {
      name: 'Paystubs',
      routeName: 'FieldPaystubs',
      icon: payrollIcon,
    },
  ]

  // Filter out the Time Tracking tab if they are a subcontractor
  if (isSubcontractor.value) {
    return allTabs.filter(
      (tab) => tab.routeName !== 'FieldTimeTracking' && tab.routeName !== 'FieldPaystubs',
    )
  }

  return allTabs
})
</script>

<style scoped>
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .pb-safe {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
